// Compact "what am I paying for domains?" widget. Renders at the top of the
// Domains tab AND the Billing tab so the user sees their recurring cost on
// the two surfaces where they'd care most.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, AlertCircle, Loader2 } from "lucide-react";

export default function DomainUsageWidget({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/domain-usage");
        if (alive) setData(data);
      } catch { /* non-fatal — widget just hides */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="nb-card-flat p-4 mb-4 flex items-center gap-2 text-sm text-gray-600" data-testid="domain-usage-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking your domain usage…
      </div>
    );
  }
  if (!data) return null;

  const fmt$ = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  const used = data.domains_used;
  const included = data.included_domains;
  const overage = Math.max(0, used - included);
  const cappedPct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  return (
    <div className="nb-card p-5 mb-4" data-testid="domain-usage-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#10B981]/10 border-2 border-black flex items-center justify-center">
            <Globe className="w-4 h-4 text-[#047857]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">Domain usage</div>
            <div className="font-display text-lg font-extrabold leading-tight">
              {used} / {included === 0 ? "0" : included} included
              {overage > 0 && <span className="text-[#FF7F50]"> · +{overage} extra</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">Monthly recurring</div>
          <div className="font-display text-xl font-extrabold leading-tight" data-testid="domain-usage-total">
            {fmt$(data.total_monthly_usd)}<span className="text-xs text-gray-500 font-bold">/mo</span>
          </div>
          {data.extras_monthly_usd > 0 && (
            <div className="text-[11px] text-gray-600 mt-0.5">
              {fmt$(data.plan_monthly_usd)} plan + {fmt$(data.extras_monthly_usd)} extras
            </div>
          )}
        </div>
      </div>

      {/* Usage bar (included slots) */}
      {included > 0 && (
        <div className="mb-2">
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all" style={{ width: `${cappedPct}%` }} />
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            {data.domains_remaining} included slot{data.domains_remaining === 1 ? "" : "s"} remaining.
          </div>
        </div>
      )}

      {!compact && data.past_due_count > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-[#FFE6EC] border-2 border-black flex items-start gap-2" data-testid="past-due-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-xs">
            <b>{data.past_due_count} extra domain subscription{data.past_due_count === 1 ? " is" : "s are"} past due.</b>{" "}
            Update your payment method in Billing to avoid the domain being suspended.
          </div>
        </div>
      )}

      {!compact && data.active_extras_count > 0 && (
        <div className="mt-3" data-testid="extras-list">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 mb-1">Active extra domains</div>
          <div className="space-y-1">
            {data.active_extras.map((e) => (
              <div key={e.sub_id} className="flex items-center justify-between text-sm">
                <span className="font-mono truncate max-w-[60%]">{e.domain}</span>
                <span className="font-semibold text-gray-700">{fmt$((e.price_cents || 0) / 100)}/mo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && overage === 0 && included > 0 && (
        <div className="mt-2 text-[11px] text-gray-500">
          Each extra domain beyond your included {included} costs <b>{fmt$(data.extra_monthly_usd_each)}/mo</b>.
        </div>
      )}
    </div>
  );
}
